<template>
  <v-main>
    <v-container fluid class="zcs">
      <!-- Header -->
      <h1 class="zcs-title">Zone Commission Settings</h1>
      <p class="zcs-sub">
        Set the revenue share for all subscription plans in a zone. Saving
        applies the split to every active plan in the selected zone.
      </p>

      <!-- Zone -->
      <v-card class="zcs-card" elevation="0">
        <div class="zcs-zone">
          <div class="zcs-zone__select">
            <div class="zcs-label">Select Zone</div>
            <v-autocomplete
              v-model="zoneId"
              :items="zoneItems"
              :loading="isMasterLoading"
              outlined
              dense
              hide-details
              placeholder="Choose a zone"
              prepend-inner-icon="mdi-map-marker"
            ></v-autocomplete>
          </div>
          <span v-if="zoneId && !isLoading" class="zcs-status" :class="`zcs-status--${status.kind}`">
            <span class="zcs-status__dot"></span>{{ status.text }}
          </span>
          <v-progress-circular v-if="isLoading" indeterminate size="22" width="2" color="primary"></v-progress-circular>
        </div>
      </v-card>

      <template v-if="zoneId">
        <!-- Allocation -->
        <v-card class="zcs-card" elevation="0">
          <div class="zcs-card__head">
            <div class="zcs-card__title">
              <v-icon color="primary" class="mr-3">mdi-chart-bar</v-icon>
              <div>
                <h2>Commission Allocation</h2>
                <p>Set the percentage share for each stakeholder. Total must be 100%.</p>
              </div>
            </div>
            <div class="zcs-note">
              <v-icon small color="primary" class="mr-2">mdi-information</v-icon>
              Changes apply to all active subscription plans in the selected zone.
            </div>
          </div>

          <div v-if="summary.mixed" class="zcs-warn">
            <v-icon small color="#b45309" class="mr-2">mdi-alert</v-icon>
            Plans in this zone use different splits today. The most common one
            is shown; saving sets every plan to the split below.
          </div>

          <div v-for="s in shares" :key="s.key" class="zcs-row">
            <div class="zcs-row__who">
              <span class="zcs-avatar" :style="{ background: s.color }">
                <img v-if="s.logo" :src="logo" alt="" />
                <v-icon v-else color="white" size="20">{{ s.icon }}</v-icon>
              </span>
              <div>
                <div class="zcs-row__name">{{ s.label }}</div>
                <div class="zcs-row__hint">{{ s.hint }}</div>
              </div>
            </div>
            <v-text-field
              v-model.number="form[s.key]"
              class="zcs-row__input"
              type="number"
              min="0"
              max="100"
              outlined
              dense
              hide-details
              suffix="%"
              @blur="clamp(s.key)"
            ></v-text-field>
            <v-slider
              v-model="form[s.key]"
              class="zcs-row__slider"
              :color="s.color"
              :track-color="'#e4e8ee'"
              min="0"
              max="100"
              step="1"
              hide-details
            ></v-slider>
            <div class="zcs-row__pct">{{ fmt(form[s.key]) }}%</div>
          </div>

          <div class="zcs-total" :class="totalOk ? 'zcs-total--ok' : 'zcs-total--bad'">
            <v-icon :color="totalOk ? '#16a34a' : '#dc2626'" size="30" class="mr-3">
              {{ totalOk ? "mdi-check-circle" : "mdi-alert-circle" }}
            </v-icon>
            <div class="zcs-total__text">
              <div class="zcs-total__title">Total Commission: {{ fmt(total) }}%</div>
              <div class="zcs-total__hint">
                <template v-if="totalOk">The total allocation is correct.</template>
                <template v-else>
                  {{ total > 100 ? "Over" : "Under" }} by {{ fmt(Math.abs(100 - total)) }}%.
                  <a v-if="walkiesBalance >= 0" @click="giveBalanceToWalkies">
                    Give the balance to Walkies
                  </a>
                </template>
              </div>
            </div>
            <div class="zcs-total__bar">
              <div class="zcs-total__fill" :style="{ width: Math.min(total, 100) + '%' }"></div>
            </div>
            <div class="zcs-total__pct">{{ fmt(total) }}%</div>
          </div>
        </v-card>

        <!-- Plans -->
        <v-card class="zcs-card" elevation="0">
          <div class="zcs-card__title">
            <v-icon color="primary" class="mr-3">mdi-layers-triple-outline</v-icon>
            <div>
              <h2>Apply to Subscription Plans</h2>
              <p>
                The commission above will be applied to the following plans in
                {{ zoneName }}:
              </p>
            </div>
          </div>
          <div class="zcs-chips">
            <span v-for="tier in tiers" :key="tier" class="zcs-chip" :class="`zcs-chip--${tier.toLowerCase()}`">
              {{ titleCase(tier) }}
            </span>
            <span class="zcs-chip zcs-chip--all">
              All {{ summary.plans.length }} active plans in {{ zoneName }}
            </span>
            <a class="zcs-link" @click="showPlans = !showPlans">
              {{ showPlans ? "Hide" : "View" }} plan-wise amounts
              <v-icon small color="primary">{{ showPlans ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </a>
          </div>

          <v-expand-transition>
            <div v-show="showPlans" class="zcs-plans">
              <table>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th class="num">Final Amount</th>
                    <th v-for="s in shares" :key="s.key" class="num">{{ s.label }}</th>
                    <th>Today</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="plan in summary.plans" :key="plan.id">
                    <td>{{ plan.no_of_dogs }} Dog {{ plan.no_of_walks }} Walk {{ titleCase(plan.tier) }}</td>
                    <td class="num">{{ rupee(plan.final_amount) }}</td>
                    <td v-for="s in shares" :key="s.key" class="num">{{ rupee(amountsFor(plan)[s.key]) }}</td>
                    <td>
                      <span v-if="plan.has_commission" class="zcs-tag zcs-tag--ok">Set, will update</span>
                      <span v-else class="zcs-tag zcs-tag--new">Not set, will add</span>
                    </td>
                  </tr>
                  <tr v-if="!summary.plans.length">
                    <td colspan="100" class="text-center">No active plans</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-expand-transition>

          <div class="zcs-info">
            <v-icon small color="primary" class="mr-2">mdi-information</v-icon>
            Subscriptions already approved keep their split; bookings approved
            after saving use the new one.
          </div>
        </v-card>

        <!-- Footer -->
        <div class="zcs-foot">
          <div class="zcs-foot__meta">
            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
            <template v-if="summary.updated_at">
              Last updated: {{ dateTime(summary.updated_at) }}
              <template v-if="summary.updated_by">
                <span class="zcs-foot__sep">|</span> Updated by: {{ summary.updated_by }}
              </template>
            </template>
            <template v-else>No commission saved for this zone yet</template>
            <a class="zcs-link ml-3" @click="openHistory">
              <v-icon small color="primary">mdi-history</v-icon> View change history
            </a>
          </div>
          <div>
            <v-btn outlined class="zcs-btn mr-3" :disabled="isSaving" @click="reset">Cancel</v-btn>
            <v-btn
              v-can:edit="'plans'"
              color="primary"
              class="zcs-btn"
              depressed
              :loading="isSaving"
              :disabled="!canSave"
              @click="save"
            >
              Save & Update All Plans
            </v-btn>
          </div>
        </div>
      </template>

      <!-- History -->
      <v-dialog v-model="historyDialog" max-width="720">
        <v-card class="zcs-history">
          <div class="zcs-history__head">
            <h2>Commission Change History &middot; {{ zoneName }}</h2>
            <v-btn icon @click="historyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div v-if="historyLoading" class="zcs-history__loading">
            <v-progress-circular indeterminate size="28" width="2" color="primary"></v-progress-circular>
          </div>
          <div v-else-if="!history.length" class="zcs-history__empty">
            No changes recorded yet for this zone.
          </div>
          <div v-else class="zcs-history__list">
            <div v-for="h in history" :key="h.id" class="zcs-history__row">
              <div class="zcs-history__meta">
                <span class="zcs-history__date">{{ dateTime(h.changed_at) }}</span>
                <span v-if="h.changed_by" class="zcs-history__by">by {{ h.changed_by }}</span>
                <span class="zcs-history__plans">{{ h.plan_count }} plan{{ h.plan_count === 1 ? "" : "s" }}</span>
              </div>
              <table class="zcs-history__table">
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="s in shares" :key="s.key">{{ s.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="h.old_split" class="zcs-history__old">
                    <td>Before</td>
                    <td v-for="s in shares" :key="s.key">{{ fmt(h.old_split[s.key]) }}%</td>
                  </tr>
                  <tr v-else class="zcs-history__old">
                    <td>Before</td>
                    <td :colspan="shares.length" class="zcs-history__none">Not set yet</td>
                  </tr>
                  <tr class="zcs-history__new">
                    <td>After</td>
                    <td v-for="s in shares" :key="s.key">{{ fmt(h.new_split[s.key]) }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
        {{ snackbar.text }}
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

const num = (v) => parseFloat(v) || 0;
const DEFAULT_SPLIT = {
  company_percent: 100,
  master_franchisee_percent: 0,
  franchisee_percent: 0,
  walkie_percent: 0,
};

export default {
  name: "ZoneCommissionSettingsPage",
  data() {
    return {
      zoneId: null,
      zoneItems: [],
      shares: [
        { key: "company_percent", label: "Walkies", hint: "Platform & Brand Share", color: "#1f5fbf", logo: true },
        { key: "master_franchisee_percent", label: "Master Franchise", hint: "Zone Level Share", color: "#8b5cf6", icon: "mdi-crown" },
        { key: "franchisee_percent", label: "Franchise", hint: "City Level Share", color: "#f97316", icon: "mdi-storefront" },
        { key: "walkie_percent", label: "Pet Walker", hint: "Service Partner Share", color: "#16a34a", icon: "mdi-walk" },
      ],
      form: { ...DEFAULT_SPLIT },
      summary: { split: null, mixed: false, plans: [], updated_at: null, updated_by: null },
      showPlans: false,
      isMasterLoading: false,
      isLoading: false,
      isSaving: false,
      snackbar: { show: false, text: "", color: "success" },
      historyDialog: false,
      historyLoading: false,
      history: [],
    };
  },
  computed: {
    // Same logo as the sidebar
    logo() {
      return this.company?.logo_path
        ? this.mediaUrl + this.company.logo_path
        : require("../../assets/petwalkie-logo.png");
    },
    total() {
      return this.shares.reduce((sum, s) => sum + num(this.form[s.key]), 0);
    },
    totalOk() {
      return Math.abs(this.total - 100) < 0.001;
    },
    walkiesBalance() {
      return 100 - (this.total - num(this.form.company_percent));
    },
    canSave() {
      return this.totalOk && !this.isLoading && this.summary.plans.length > 0;
    },
    zoneName() {
      const zone = this.zoneItems.find((z) => z.value === this.zoneId);
      return zone ? this.titleCase(zone.text) + " Zone" : "this zone";
    },
    tiers() {
      return [...new Set(this.summary.plans.map((p) => (p.tier || "").toUpperCase()).filter(Boolean))];
    },
    status() {
      const plans = this.summary.plans;
      const set = plans.filter((p) => p.has_commission).length;
      if (!plans.length || !set) return { kind: "none", text: "Not set" };
      if (set < plans.length) return { kind: "part", text: `Set on ${set} of ${plans.length} plans` };
      return { kind: "ok", text: "Active" };
    },
  },
  watch: {
    zoneId(id) {
      if (id) this.getSummary();
    },
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    fmt(v) {
      return parseFloat(num(v).toFixed(2));
    },
    titleCase(text) {
      return (text || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    },
    rupee(v) {
      return "₹" + num(v).toLocaleString("en-IN");
    },
    dateTime(v) {
      return new Date(v).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
      });
    },
    clamp(key) {
      this.form[key] = Math.min(100, Math.max(0, num(this.form[key])));
    },
    giveBalanceToWalkies() {
      this.form.company_percent = this.fmt(this.walkiesBalance);
    },
    // Same split as the server: round each share, Walkies absorbs the rounding
    amountsFor(plan) {
      const total = num(plan.final_amount);
      const amounts = {};
      let given = 0;
      this.shares.forEach((s) => {
        if (s.key === "company_percent") return;
        amounts[s.key] = Math.round((total * num(this.form[s.key])) / 100);
        given += amounts[s.key];
      });
      amounts.company_percent = total - given;
      return amounts;
    },
    reset() {
      this.form = { ...DEFAULT_SPLIT, ...(this.summary.split || {}) };
    },
    async getMasters() {
      this.isMasterLoading = true;
      await axios
        .get(`masters?context=zone_commissions`)
        .then((masters) => {
          this.zoneItems = masters.data.zones.map((zone) => ({ text: zone.description, value: zone.id }));
          if (this.zoneItems.length) this.zoneId = this.zoneItems[0].value;
        })
        .catch((error) => {
          console.error(error);
          this.isUnAuthorised();
        });
      this.isMasterLoading = false;
    },
    async getSummary() {
      this.isLoading = true;
      await axios
        .get(`plan_zone_commissions/zone_summary?zone_id=${this.zoneId}`)
        .then((response) => {
          this.summary = response.data.data;
          this.reset();
        })
        .catch((error) => {
          console.error(error);
          this.isUnAuthorised();
        });
      this.isLoading = false;
    },
    async openHistory() {
      this.historyDialog = true;
      this.historyLoading = true;
      await axios
        .get(`plan_zone_commissions/zone_history?zone_id=${this.zoneId}`)
        .then((response) => {
          this.history = response.data.data;
        })
        .catch((error) => {
          console.error(error);
        });
      this.historyLoading = false;
    },
    async save() {
      this.isSaving = true;
      const payload = {
        zone_ids: [this.zoneId],
        plan_ids: this.summary.plans.map((p) => p.id),
        walkie_percent: num(this.form.walkie_percent),
        franchisee_percent: num(this.form.franchisee_percent),
        master_franchisee_percent: num(this.form.master_franchisee_percent),
        overwrite: true,
      };
      await axios
        .post("/plan_zone_commissions/bulk", payload)
        .then(async (response) => {
          const r = response.data.data;
          await this.saveUserTimestamp(this.summary.split, { zone_id: this.zoneId, ...this.form });
          this.snackbar = {
            show: true,
            color: "success",
            text: `Commission saved for ${r.created + r.updated} plans in ${this.zoneName}.`,
          };
          await this.getSummary();
        })
        .catch((error) => {
          if (error.response?.status === 422) {
            this.snackbar = { show: true, color: "error", text: error.response.data.message };
          } else {
            console.error(error);
            this.isUnAuthorised();
          }
        });
      this.isSaving = false;
    },
  },
};
</script>

<style scoped>
.zcs {
  color: #1a2436;
}

.zcs-title {
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 2px;
}

.zcs-sub {
  font-size: 0.875rem;
  color: #66738a;
  margin-bottom: 16px;
}

.zcs-card {
  border: 1px solid #e4e8ee !important;
  border-radius: 14px !important;
  padding: 18px 20px;
  margin-bottom: 14px;
}

.zcs-zone {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.zcs-zone__select {
  width: 420px;
  max-width: 100%;
}

.zcs-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.zcs-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
}

.zcs-status__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: currentColor;
}

.zcs-status--ok { background: #e8f7ee; color: #16a34a; }
.zcs-status--part { background: #fff4e5; color: #c2410c; }
.zcs-status--none { background: #f1f3f6; color: #66738a; }

.zcs-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.zcs-card__title {
  display: flex;
  align-items: flex-start;
}

.zcs-card__title h2 {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
}

.zcs-card__title p {
  font-size: 0.85rem;
  color: #66738a;
  margin: 2px 0 0;
}

.zcs-note,
.zcs-info {
  display: flex;
  align-items: center;
  background: #eaf2fc;
  color: #1f4fa3;
  font-size: 0.82rem;
  border-radius: 10px;
  padding: 10px 14px;
}

.zcs-info {
  margin-top: 14px;
}

.zcs-warn {
  display: flex;
  align-items: center;
  background: #fff8e6;
  color: #92400e;
  font-size: 0.82rem;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 10px;
}

.zcs-row {
  display: grid;
  grid-template-columns: minmax(220px, 1.3fr) 110px minmax(160px, 3fr) 70px;
  align-items: center;
  gap: 24px;
  border: 1px solid #edf0f4;
  border-radius: 12px;
  padding: 10px 18px;
  margin-bottom: 8px;
}

.zcs-row__who {
  display: flex;
  align-items: center;
  gap: 14px;
}

.zcs-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 auto;
}

.zcs-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.zcs-row__name {
  font-weight: 700;
}

.zcs-row__hint {
  font-size: 0.8rem;
  color: #66738a;
}

.zcs-row__pct {
  font-size: 1.1rem;
  font-weight: 700;
  text-align: right;
}

.zcs-total {
  display: grid;
  grid-template-columns: auto minmax(200px, 1.3fr) minmax(160px, 3fr) 70px;
  align-items: center;
  gap: 16px;
  border-radius: 12px;
  padding: 12px 18px;
  margin-top: 4px;
}

.zcs-total--ok { background: #e8f7ee; border: 1px solid #bfe8cd; }
.zcs-total--bad { background: #fdecec; border: 1px solid #f5c2c2; }

.zcs-total__title {
  font-weight: 700;
}

.zcs-total--ok .zcs-total__title,
.zcs-total--ok .zcs-total__pct { color: #15803d; }
.zcs-total--bad .zcs-total__title,
.zcs-total--bad .zcs-total__pct { color: #b91c1c; }

.zcs-total__hint {
  font-size: 0.82rem;
  color: #4b5563;
}

.zcs-total__hint a {
  margin-left: 6px;
  font-weight: 600;
}

.zcs-total__bar {
  height: 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.zcs-total__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s;
}

.zcs-total--ok .zcs-total__fill { background: #16a34a; }
.zcs-total--bad .zcs-total__fill { background: #dc2626; }

.zcs-total__pct {
  font-size: 1.1rem;
  font-weight: 700;
  text-align: right;
}

.zcs-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 14px 0 0 36px;
}

.zcs-chip {
  padding: 6px 18px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #eef0f3;
  color: #374151;
}

.zcs-chip--gold { background: #fdf0c8; color: #92400e; }
.zcs-chip--platinium,
.zcs-chip--platinum { background: #f1e8fd; color: #6d28d9; }
.zcs-chip--all { background: #e3edfb; color: #1f4fa3; }

.zcs-link {
  font-size: 0.82rem;
  font-weight: 600;
}

.zcs-plans {
  margin: 14px 0 0;
  overflow-x: auto;
}

.zcs-plans table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.zcs-plans th {
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #66738a;
  padding: 8px 10px;
  border-bottom: 1px solid #e4e8ee;
  white-space: nowrap;
}

.zcs-plans td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0f2f5;
  white-space: nowrap;
}

.zcs-plans .num {
  text-align: right;
}

.zcs-tag {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
}

.zcs-tag--ok { background: #e8f7ee; color: #15803d; }
.zcs-tag--new { background: #e3edfb; color: #1f4fa3; }

.zcs-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 4px 2px 16px;
}

.zcs-foot__meta {
  font-size: 0.82rem;
  color: #66738a;
  display: flex;
  align-items: center;
}

.zcs-foot__sep {
  margin: 0 8px;
}

.zcs-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  border-radius: 8px;
}

.zcs-history {
  padding: 18px 22px 22px;
}

.zcs-history__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.zcs-history__head h2 {
  font-size: 1.1rem;
  font-weight: 700;
}

.zcs-history__loading,
.zcs-history__empty {
  display: flex;
  justify-content: center;
  padding: 32px 0;
  color: #66738a;
  font-size: 0.875rem;
}

.zcs-history__list {
  max-height: 60vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.zcs-history__row {
  border: 1px solid #e4e8ee;
  border-radius: 10px;
  padding: 10px 14px;
}

.zcs-history__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  color: #66738a;
  margin-bottom: 8px;
}

.zcs-history__date {
  font-weight: 700;
  color: #1a2436;
}

.zcs-history__plans {
  margin-left: auto;
  background: #f1f3f6;
  border-radius: 999px;
  padding: 2px 10px;
  font-weight: 600;
}

.zcs-history__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.zcs-history__table th {
  text-align: right;
  color: #8d99ad;
  font-weight: 600;
  padding: 2px 8px;
}

.zcs-history__table th:first-child {
  text-align: left;
}

.zcs-history__table td {
  text-align: right;
  padding: 4px 8px;
  font-variant-numeric: tabular-nums;
}

.zcs-history__table td:first-child {
  text-align: left;
  font-weight: 600;
  color: #66738a;
}

.zcs-history__old td {
  color: #9aa3b2;
  text-decoration: line-through;
}

.zcs-history__new td {
  color: #15803d;
  font-weight: 700;
}

.zcs-history__none {
  text-decoration: none !important;
  font-style: italic;
}

@media (max-width: 900px) {
  .zcs-row {
    grid-template-columns: 1fr 90px;
    gap: 10px 16px;
  }

  .zcs-total {
    grid-template-columns: auto 1fr auto;
    gap: 10px 12px;
  }

  .zcs-total__bar {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .zcs-chips {
    margin-left: 0;
  }
}
</style>
